import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import SEO from '../components/SEO';
import LoadingSpinner from '../components/LoadingSpinner';
import { Product } from '../types';
import API_URL from '../config/api';

interface Category {
  id: number;
  name: string;
  slug: string;
}

const AdminProductEditPage: React.FC = () => {
  const { id, action } = useParams<{ id?: string; action: string }>();
  const isNewProduct = action === 'new';
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [categories, setCategories] = useState<Category[]>([]);
  
  // Form state
  const [formData, setFormData] = useState<{
    name: string;
    description: string;
    price: string;
    rating: string;
    image: string;
    category: string;
    amazonLink: string;
    healthScore: string;
    ingredients: string;
    benefits: string;
    tags: string;
    asin: string;
  }>({
    name: '',
    description: '',
    price: '',
    rating: '',
    image: '',
    category: '',
    amazonLink: '',
    healthScore: '',
    ingredients: '',
    benefits: '',
    tags: '',
    asin: ''
  });
  
  // Check authentication
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin');
    }
  }, [navigate]);
  
  // Fetch product data and categories
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('adminToken');
        
        // Fetch categories
        const categoriesResponse = await fetch(`${API_URL}/categories`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!categoriesResponse.ok) {
          throw new Error('Failed to fetch categories');
        }
        
        const categoriesData = await categoriesResponse.json();
        setCategories(categoriesData);
        
        // If editing existing product, fetch product data
        if (!isNewProduct && id) {
          const productResponse = await fetch(`${API_URL}/products/${id}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (!productResponse.ok) {
            throw new Error('Failed to fetch product');
          }
          
          const productData: Product = await productResponse.json();
          
          setFormData({
            name: productData.name,
            description: productData.description,
            price: productData.price.toString(),
            rating: productData.rating.toString(),
            image: productData.image,
            category: productData.category,
            amazonLink: productData.amazonLink,
            healthScore: productData.healthScore.toString(),
            ingredients: productData.ingredients ? productData.ingredients.join(', ') : '',
            benefits: productData.benefits ? productData.benefits.join(', ') : '',
            tags: productData.tags ? productData.tags.join(', ') : '',
            asin: productData.asin || ''
          });
        }
        
        setLoading(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id, isNewProduct, navigate]);
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    
    try {
      const token = localStorage.getItem('adminToken');
      
      // Convert form data to API format
      const productData: any = {
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        rating: parseFloat(formData.rating),
        image: formData.image,
        category: formData.category,
        amazonLink: formData.amazonLink,
        healthScore: parseInt(formData.healthScore),
        ingredients: formData.ingredients.split(',').map(item => item.trim()).filter(Boolean),
        benefits: formData.benefits.split(',').map(item => item.trim()).filter(Boolean),
        tags: formData.tags.split(',').map(item => item.trim()).filter(Boolean),
        asin: formData.asin || null
      };
      
      // Create or update product
      const url = isNewProduct 
        ? `${API_URL}/products` 
        : `${API_URL}/products/${id}`;
      
      const method = isNewProduct ? 'POST' : 'PUT';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(productData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save product');
      }
      
      // Navigate back to products list
      navigate('/admin/products');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save product');
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen py-12">
        <div className="container-custom">
          <div className="bg-white rounded-lg shadow-md p-8 flex justify-center">
            <LoadingSpinner />
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="bg-gray-50 min-h-screen py-12">
      <SEO
        title={isNewProduct ? 'Add New Product' : 'Edit Product'}
        description="Manage products in the GoodCart admin panel"
        url={`https://goodcart.org/admin/products/${isNewProduct ? 'new' : `edit/${id}`}`}
      />
      
      <div className="container-custom">
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-6 bg-gray-800 text-white">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0 mb-2">
              <h1 className="text-2xl font-bold">{isNewProduct ? 'Add New Product' : 'Edit Product'}</h1>
              <div className="flex space-x-4">
                <Link
                  to="/admin/products"
                  className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </Link>
              </div>
            </div>
          </div>

          <div className="bg-gray-100 p-3 border-b border-gray-200">
            <Link
              to="/admin/products"
              className="flex items-center text-brand-green-600 hover:text-brand-green-800 font-medium"
            >
              <span className="mr-2">←</span> Back to Products
            </Link>
          </div>
          
          {error && (
            <div className="p-4 bg-red-50 text-red-700 border-b border-red-100">
              {error}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="col-span-1 md:col-span-2">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Product Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-green-500"
                />
              </div>
              
              <div className="col-span-1 md:col-span-2">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                  Description *
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-green-500"
                />
              </div>
              
              <div>
                <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1">
                  Price *
                </label>
                <input
                  type="number"
                  id="price"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  required
                  min="0"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-green-500"
                />
              </div>
              
              <div>
                <label htmlFor="rating" className="block text-sm font-medium text-gray-700 mb-1">
                  Rating
                </label>
                <input
                  type="number"
                  id="rating"
                  name="rating"
                  value={formData.rating}
                  onChange={handleInputChange}
                  min="0"
                  max="5"
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-green-500"
                />
              </div>
              
              <div>
                <label htmlFor="image" className="block text-sm font-medium text-gray-700 mb-1">
                  Image URL
                </label>
                <input
                  type="url"
                  id="image"
                  name="image"
                  value={formData.image}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-green-500"
                />
              </div>
              
              <div>
                <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                  Category *
                </label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-green-500"
                >
                  <option value="">Select a category</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.slug}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label htmlFor="amazonLink" className="block text-sm font-medium text-gray-700 mb-1">
                  Amazon Link *
                </label>
                <input
                  type="url"
                  id="amazonLink"
                  name="amazonLink"
                  value={formData.amazonLink}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-green-500"
                />
              </div>
              
              <div>
                <label htmlFor="asin" className="block text-sm font-medium text-gray-700 mb-1">
                  Amazon ASIN
                </label>
                <input
                  type="text"
                  id="asin"
                  name="asin"
                  value={formData.asin}
                  onChange={handleInputChange}
                  maxLength={10}
                  placeholder="e.g. B08N5WRWNW"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-green-500"
                />
              </div>
              
              <div>
                <label htmlFor="healthScore" className="block text-sm font-medium text-gray-700 mb-1">
                  Health Score
                </label>
                <input
                  type="number"
                  id="healthScore"
                  name="healthScore"
                  value={formData.healthScore}
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-green-500"
                />
              </div>
              
              <div className="col-span-1 md:col-span-2">
                <label htmlFor="ingredients" className="block text-sm font-medium text-gray-700 mb-1">
                  Ingredients (comma separated)
                </label>
                <input
                  type="text"
                  id="ingredients"
                  name="ingredients"
                  value={formData.ingredients}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-green-500"
                  placeholder="e.g. Organic Quinoa, Flax Seeds, Himalayan Salt"
                />
              </div>
              
              <div className="col-span-1 md:col-span-2">
                <label htmlFor="benefits" className="block text-sm font-medium text-gray-700 mb-1">
                  Benefits (comma separated)
                </label>
                <input
                  type="text"
                  id="benefits"
                  name="benefits"
                  value={formData.benefits}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-green-500"
                  placeholder="e.g. High in protein, Rich in fiber, Contains all 9 essential amino acids"
                />
              </div>
              
              <div className="col-span-1 md:col-span-2">
                <label htmlFor="tags" className="block text-sm font-medium text-gray-700 mb-1">
                  Tags (comma separated)
                </label>
                <input
                  type="text"
                  id="tags"
                  name="tags"
                  value={formData.tags}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-green-500"
                  placeholder="e.g. organic, gluten-free, vegan, non-gmo"
                />
              </div>
            </div>
            
            <div className="mt-8 flex justify-end">
              <button
                type="submit"
                disabled={saving}
                className={`btn btn-primary ${saving ? 'opacity-75 cursor-not-allowed' : ''}`}
              >
                {saving ? 'Saving...' : 'Save Product'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminProductEditPage;
